import { AlertTriangle, CheckCircle2, MinusCircle } from "lucide-react";
import type { ReactNode } from "react";

import { cn } from "@/lib/utils";

import {
  GRADE_LABEL,
  benchmarkText,
  grade,
  isImprovement,
  type Grade,
  type Metric,
} from "./performance";

const GRADE_STYLE: Record<Grade, string> = {
  good: "bg-emerald-100 text-emerald-900 dark:bg-emerald-400/15 dark:text-emerald-100",
  ok: "bg-amber-100 text-amber-900 dark:bg-amber-400/15 dark:text-amber-100",
  low: "bg-red-100 text-red-900 dark:bg-red-400/15 dark:text-red-100",
};

const GRADE_ICON = {
  good: CheckCircle2,
  ok: MinusCircle,
  low: AlertTriangle,
};

export const GradeBadge = ({ value }: { value: Grade | null }) => {
  if (!value) return null;
  const Icon = GRADE_ICON[value];
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium",
        GRADE_STYLE[value],
      )}
    >
      <Icon className="h-3 w-3" />
      {GRADE_LABEL[value]}
    </span>
  );
};

interface KpiTileProps {
  label: string;
  value: string;
  /** Raw number used for the grade (rate, minutes…) */
  raw?: number | null;
  metric?: Metric;
  detail?: ReactNode;
  /** Change against the previous period, in the metric's own unit */
  change?: number | null;
  changeUnit?: string;
  /** For counts without a reference: more is better */
  moreIsBetter?: boolean;
}

/** One number with its grade, its reference and the change vs before. */
export const KpiTile = ({
  label,
  value,
  raw,
  metric,
  detail,
  change,
  changeUnit = "",
  moreIsBetter = true,
}: KpiTileProps) => {
  const level = metric ? grade(metric, raw ?? null) : null;
  const better =
    change == null || change === 0
      ? null
      : metric
        ? isImprovement(metric, change)
        : moreIsBetter
          ? change > 0
          : change < 0;
  return (
    <div className="flex min-h-36 flex-col gap-1.5 rounded-3xl border bg-card p-4">
      <div className="flex items-start justify-between gap-2">
        <span className="text-sm font-medium leading-tight">{label}</span>
        <GradeBadge value={level} />
      </div>
      <span className="text-3xl font-semibold tabular-nums">{value}</span>
      {change != null ? (
        <span
          className={cn(
            "text-xs font-medium tabular-nums",
            better == null
              ? "text-muted-foreground"
              : better
                ? "text-emerald-700 dark:text-emerald-300"
                : "text-red-700 dark:text-red-300",
          )}
        >
          {change > 0 ? "↑" : change < 0 ? "↓" : "="}{" "}
          {change === 0
            ? "igual"
            : `${Math.abs(change).toLocaleString("es-ES")}${changeUnit}`}{" "}
          vs periodo anterior
        </span>
      ) : null}
      {detail ? (
        <span className="text-xs text-muted-foreground">{detail}</span>
      ) : null}
      {metric ? (
        <span className="mt-auto text-[11px] text-muted-foreground">
          {benchmarkText(metric)}
        </span>
      ) : null}
    </div>
  );
};
